import React from 'react';
import { map, get, noop, isNull, filter, startsWith, toLower, isEmpty, size } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { button as Button } from 'modules/button';
import { checkbox as Checkbox } from 'ui/views/generic/checkbox';
import { getLabel } from 'utils/getLabel';
import { GLOBAL_LABELS, DATA_TYPES } from 'constants/common';
import { Typeahead } from 'modules/typeahead';
import { searchInput as SearchInput } from 'ui/views/generic/searchInput';
import { Icon } from 'modules/icon-bank';
import { textWithPatternMatch } from 'utils/textWithPatternMatch';
import { spinner as Spinner } from 'ui/views/generic/spinner';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { cagFilterOverlay as CagFilterOverlayView } from './cagFilterOverlay.view';
 *
 * // render
 * <CagFilterOverlayView propName='propValue'/>
 */
export const cagFilterOverlay = ( props ) => {

    // steps class name
    const stepsClassName = classnames( 'ui-cag-filter-overlay__steps', {
        'ui-cag-filter-overlay__steps--disabled': props.isAllSelected,
    } );

    return (
        <div className='ui-cag-filter-overlay'>
            {/* all selection */}
            <div className='ui-cag-filter-overlay__all-selection'>
                <Checkbox
                    className='ui-cag-filter-overlay__all-selection__checkbox'
                    value={ props.isAllSelected }
                    onClick={ props.onAllSelection }
                    size='xs'
                />

                <p className='ui-cag-filter-overlay__all-selection__title'>
                    { getLabel( GLOBAL_LABELS.DISPLAY_TEXT_ALL ) }
                </p>
            </div>

            {/* selection steps */}
            <div className={ stepsClassName }>

                {/* carrier */}
                <div className='ui-cag-filter-overlay__steps__carrier'>
                    {/* title */}
                    <div className='ui-cag-filter-overlay__steps__carrier__title'>
                        { getLabel( get( props, 'steps.carrier.title' ) ) }
                    </div>

                    {/* typeahead */}
                    <Typeahead
                        type={ DATA_TYPES.MULTI_SELECT }
                        className='ui-cag-filter-overlay__steps__carrier__typeahead'
                        url={ get( props, 'steps.carrier.url' ) }
                        placeholder={ get( props, 'steps.carrier.placeholder' ) }
                        onChange={ ( payload ) => props.onTypeaheadSelection( payload ) }
                        ignoreQueryChangeOnSelect={ true }
                        ignoreOptions={ map( get( props, 'steps.carrier.options' ), 'value' ) }
                    />

                    {/* choice list */}
                    <div className='ui-cag-filter-overlay__steps__carrier__choice-list'>
                        {
                            map( get( props, 'steps.carrier.options' ), ( option ) => {

                                // arrow icon class name
                                const arrowClassName = classnames( 'ui-cag-filter-overlay__steps__carrier__choice-list__option__arrow', {
                                    'ui-cag-filter-overlay__steps__carrier__choice-list__option__arrow--active': option.isActive,
                                } );

                                return (
                                    <div
                                        id={ option.id }
                                        key={ option.id }
                                        className='ui-cag-filter-overlay__steps__carrier__choice-list__option'
                                        onClick={ () => props.onOptionClick( {
                                            step: 'carrier',
                                            option: option,
                                        } ) }
                                    >

                                        {/* checkbox */}
                                        <Checkbox
                                            className='ui-cag-filter-overlay__steps__carrier__choice-list__option__checkbox'
                                            value={ isNull( option.isSelected ) ? undefined : get( option, 'isSelected', false ) }
                                            size='xs'
                                            onClick={ ( event ) => {
                                                event.stopPropagation();
                                                props.onCheckboxClick( {
                                                    step: 'carrier',
                                                    option: option,
                                                } );
                                            } }
                                        />

                                        {/* option title */}
                                        <p className='ui-cag-filter-overlay__steps__carrier__choice-list__option__title'>
                                            { getLabel( option.title ) }
                                        </p>
                                        
                                        {/* arrow */}
                                        {
                                            option.isLeaf ? null : (
                                                <div className={ arrowClassName }>
                                                    <Icon name='downArrow' width='12' height='9' />
                                                </div>
                                            )
                                        }
                                        {
                                            option.isFetching && (
                                                <div className='ui-cag-filter-overlay__steps__carrier__choice-list__option__fetching'>
                                                    <Spinner size={ 'xs' } />
                                                </div>
                                            )
                                        }
                                    </div>
                                );
                            } )
                        }
                    </div>
                </div>

                {/* account */}
                <div className='ui-cag-filter-overlay__steps__account'>
                    {/* title */}
                    <div className='ui-cag-filter-overlay__steps__account__title'>
                        { getLabel( get( props, 'steps.account.title' ) ) }
                    </div>

                    {/* typeahead */}
                    <SearchInput
                        className='ui-cag-filter-overlay__steps__account__search'
                        placeholder={ getLabel( get( props, 'steps.account.placeholder' ) ) }
                        value={ get( props, 'accountSearchQuery', '' ) }
                        borderless={ false }
                        onChange={ ( searchQuery ) => props.onSearchQueryChange( {
                            step: 'account',
                            searchQuery: searchQuery,
                        } ) }
                        disabled= { 0 === size( get( props, 'steps.account.options', [] ) ) }
                    />

                    {/* choice list */}
                    <div className='ui-cag-filter-overlay__steps__account__choice-list'>
                        {
                            map( get( props, 'steps.account.options' ), ( group ) => {

                                // filtered account options
                                const _filteredAccountOptions = filter( group.options, _option => startsWith( toLower( getLabel( _option.title ) ), toLower( get( props, 'accountSearchQuery', '' ) ) ) );

                                // return `null` if no options to display
                                if( isEmpty( _filteredAccountOptions ) ) {
                                    return null;
                                }

                                return (
                                    <div key={ group.id } className='ui-cag-filter-overlay__steps__account__choice-list__group'>
                                        {/* group title */}
                                        <div className='ui-cag-filter-overlay__steps__account__choice-list__group__title'>
                                            { getLabel( group.title ) }
                                        </div>

                                        {/* group options */}
                                        <div className='ui-cag-filter-overlay__steps__account__choice-list__group__options'>
                                            {
                                                map( _filteredAccountOptions, option => {

                                                    // arrow icon class name
                                                    const arrowClassName = classnames( 'ui-cag-filter-overlay__steps__account__choice-list__group__options__option__arrow', {
                                                        'ui-cag-filter-overlay__steps__account__choice-list__group__options__option__arrow--active': option.isActive,
                                                    } );

                                                    return (
                                                        <div
                                                            key={ option.id }
                                                            className='ui-cag-filter-overlay__steps__account__choice-list__group__options__option'
                                                            onClick={ () => props.onOptionClick( {
                                                                step: 'account',
                                                                group: group,
                                                                option: option,
                                                            } ) }
                                                        >
                                                            {/* checkbox */}
                                                            <Checkbox
                                                                className='ui-cag-filter-overlay__steps__account__choice-list__group__options__option__checkbox'
                                                                value={ isNull( option.isSelected ) ? undefined : get( option, 'isSelected', false ) }
                                                                size='xs'
                                                                onClick={ ( event ) => {
                                                                    event.stopPropagation();
                                                                    props.onCheckboxClick( {
                                                                        step: 'account',
                                                                        group: group,
                                                                        option: option,
                                                                    } );
                                                                } }
                                                            />

                                                            {/* option title */}
                                                            <p
                                                                className='ui-cag-filter-overlay__steps__account__choice-list__group__options__option__title'
                                                                dangerouslySetInnerHTML={ {
                                                                    __html: textWithPatternMatch( { value: getLabel( option.title ), pattern: get( props, 'accountSearchQuery', '' ), wildcard: false } )
                                                                } }
                                                            />
                                            
                                                            {/* arrow */}
                                                            {
                                                                option.isLeaf ? null : (
                                                                    <div className={ arrowClassName }>
                                                                        <Icon name='downArrow' width='12' height='9' />
                                                                    </div>
                                                                )
                                                            }
                                                            {
                                                                option.isFetching && (
                                                                    <div className='ui-cag-filter-overlay__steps__carrier__choice-list__option__fetching'>
                                                                        <Spinner size={ 'xs' } />
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    );
                                                } )
                                            }
                                        </div>
                                    </div>
                                );
                            } )
                        }
                    </div>
                </div>

                {/* group */}
                <div className='ui-cag-filter-overlay__steps__group'>
                    {/* title */}
                    <div className='ui-cag-filter-overlay__steps__group__title'>
                        { getLabel( get( props, 'steps.group.title' ) ) }
                    </div>

                    {/* typeahead */}
                    <SearchInput
                        className='ui-cag-filter-overlay__steps__group__search'
                        placeholder={ getLabel( get( props, 'steps.group.placeholder' ) ) }
                        value={ get( props, 'groupSearchQuery', '' ) }
                        borderless={ false }
                        onChange={ ( searchQuery ) => props.onSearchQueryChange( {
                            step: 'group',
                            searchQuery: searchQuery,
                        } ) }
                        disabled= { 0 === size( get( props, 'steps.group.options', [] ) ) }

                    />

                    {/* choice list */}
                    <div className='ui-cag-filter-overlay__steps__group__choice-list'>
                        {
                            map( get( props, 'steps.group.options' ), ( group ) => {

                                // filtered group options
                                const _filteredGroupOptions = filter( group.options, _option => startsWith( toLower( getLabel( _option.title ) ), toLower( get( props, 'groupSearchQuery', '' ) ) ) );

                                // return `null` if no options to display
                                if( isEmpty( _filteredGroupOptions ) ) {
                                    return null;
                                }

                                return (
                                    <div id={ group.id } key={ group.id } className='ui-cag-filter-overlay__steps__group__choice-list__group'>
                                        {/* group title */}
                                        <div className='ui-cag-filter-overlay__steps__group__choice-list__group__title'>
                                            { getLabel( group.title ) }
                                        </div>

                                        {/* group options */}
                                        <div className='ui-cag-filter-overlay__steps__group__choice-list__group__options'>
                                            {
                                                map( _filteredGroupOptions, option => {
                                                    return (
                                                        <div
                                                            key={ option.id }
                                                            className='ui-cag-filter-overlay__steps__group__choice-list__group__options__option'
                                                            onClick={ () => props.onOptionClick( {
                                                                step: 'group',
                                                                group: group,
                                                                option: option,
                                                            } ) }
                                                        >
                                                            {/* checkbox */}
                                                            <Checkbox
                                                                className='ui-cag-filter-overlay__steps__group__choice-list__group__options__option__checkbox'
                                                                value={ isNull( option.isSelected ) ? undefined : get( option, 'isSelected', false ) }
                                                                size='xs'
                                                                onClick={ ( event ) => {
                                                                    event.stopPropagation();
                                                                    props.onCheckboxClick( {
                                                                        step: 'group',
                                                                        group: group,
                                                                        option: option,
                                                                    } );
                                                                } }
                                                            />

                                                            {/* option title */}
                                                            <p
                                                                className='ui-cag-filter-overlay__steps__group__choice-list__group__options__option__title'
                                                                dangerouslySetInnerHTML={ {
                                                                    __html: textWithPatternMatch( { value: getLabel( option.title ), pattern: get( props, 'groupSearchQuery', '' ), wildcard: false } )
                                                                } }
                                                            />
                                                        </div>
                                                    );
                                                } )
                                            }
                                        </div>
                                    </div>
                                );
                            } )
                        }
                    </div>
                </div>
            </div>

            {/* actions */}
            <div className='ui-cag-filter-overlay__actions'>
                {
                    map( props.actions, action => {
                        return (
                            <Button
                                key={ action.name }
                                id={ action.name }
                                type='button'
                                className='ui-cag-filter-overlay__actions__action'
                                size='medium'
                                { ...action }
                                disabled={ 'cancel' === action.name ? false: props.disableApply }
                                outline={ 'cancel' === action.name ? true: false }
                                onClick = { () => props.onActionClick( action ) }
                            />
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
cagFilterOverlay.displayName = 'cagFilterOverlay';

// set default props
cagFilterOverlay.defaultProps = {
    steps: {},
    disableApply: false,
    isAllSelected: false,
    closeOverlay: noop,
    onActionClick: noop,
    onAllSelection: noop,
    onOptionClick: noop,
    onCheckboxClick: noop,
    onSearchQueryChange: noop,
    onTypeaheadSelection: noop,
};

// prop types
cagFilterOverlay.propTypes = {};
